import { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useFloatingBanners } from "@/features/banner/api/useFloatingBanners";
import { useBannerActions } from "@/features/banner/api/useBannerActions";
import {
  isSafeHttpUrl,
  bannerAltText,
  generateImpressionId,
} from "@/utils/bannerUrl";

/**
 * ログイン後に表示するお知らせモーダル（表示位置 login_modal）。
 * 閉じるとその日は再表示しない(hidden)。「今後表示しない」にチェックして
 * 閉じると恒久的に表示しない(dismissed)。画像クリックでリンク先を新規タブで開く。
 */
export const LoginAnnouncementModal: React.FC = () => {
  const { banners, recordAction, removeBannerFromCache } =
    useFloatingBanners("login_modal");
  const { recordEvent } = useBannerActions();
  const [closedIds, setClosedIds] = useState<Set<number>>(new Set());
  // 「今後表示しない」の選択はバナーIDごとに保持する。閉じると次のバナーへ進むため
  // 複数の永続化リクエストが重なり得る。共有の真偽値だと、失敗時の復元が
  // 別バナーの選択として反映され、意図しない dismissed が送られてしまう。
  const [dontShowAgainIds, setDontShowAgainIds] = useState<Set<number>>(
    new Set()
  );
  const impressionIdRef = useRef<string>(generateImpressionId());
  const firedForRef = useRef<number | null>(null);

  const visibleBanners = banners.filter(
    (b) =>
      !closedIds.has(b.id) &&
      isSafeHttpUrl(b.imageUrl) &&
      isSafeHttpUrl(b.linkUrl)
  );
  const banner = visibleBanners[0] ?? null;

  // 表示中バナーごとに impression を1回計測（バナー切替時は表示IDを採番し直す）
  useEffect(() => {
    if (!banner) return;
    if (firedForRef.current === banner.id) return;
    firedForRef.current = banner.id;
    impressionIdRef.current = generateImpressionId();
    void recordEvent(
      banner.id,
      "impression",
      impressionIdRef.current,
      "login_modal"
    ).catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [banner?.id]);

  if (!banner) return null;

  const restore = (id: number): void => {
    setClosedIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const setDontShowAgain = (id: number, checked: boolean): void => {
    setDontShowAgainIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const close = (id: number): void => {
    // 「今後表示しない」選択時は恒久非表示(dismissed)、それ以外は当日非表示(hidden)。
    // 楽観的に閉じ、永続化に失敗したら再表示してサーバ状態との不整合を防ぐ。
    const wasDismiss = dontShowAgainIds.has(id);
    const action = wasDismiss ? "dismissed" : "hidden";
    setClosedIds((prev) => new Set(prev).add(id));
    setDontShowAgain(id, false);
    recordAction(id, action)
      // 成功時はキャッシュからも除外し、画面再訪時の復活を防ぐ。
      .then(() => {
        removeBannerFromCache(id);
      })
      .catch(() => {
        // 再表示に加えて、そのバナー自身の「今後表示しない」の選択も復元する。
        // 復元しないと、再度閉じた際に dismissed ではなく hidden が送信される。
        restore(id);
        if (wasDismiss) setDontShowAgain(id, true);
      });
  };

  return (
    <Dialog
      open
      onClose={() => {
        close(banner.id);
      }}
      fullWidth
      maxWidth="sm"
      // aria-label は role="dialog" を持つ Paper 自体に付与する。
      // Dialog 直下の aria-label は外側 Modal に渡り、ダイアログが無名になるため。
      PaperProps={{ "aria-label": "お知らせ" }}
      slotProps={{
        backdrop: { sx: { backgroundColor: "rgba(0, 0, 0, 0.72)" } },
      }}
    >
      <Box sx={{ position: "relative" }}>
        <IconButton
          aria-label="閉じる"
          onClick={() => {
            close(banner.id);
          }}
          sx={{
            position: "absolute",
            top: 4,
            right: 4,
            zIndex: 1,
            color: "#fff",
            bgcolor: "rgba(0,0,0,0.4)",
          }}
          size="small"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <DialogContent sx={{ p: 0 }}>
          <Box
            component="a"
            href={banner.linkUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              // クリック記録の失敗は握りつぶす（遷移は妨げない）
              void recordEvent(
                banner.id,
                "click",
                impressionIdRef.current,
                "login_modal"
              ).catch(() => undefined);
              void recordAction(banner.id, "clicked").catch(() => undefined);
            }}
            sx={{ display: "block", lineHeight: 0 }}
          >
            <Box
              component="img"
              src={banner.imageUrl}
              alt={bannerAltText(banner.linkUrl)}
              sx={{
                width: "100%",
                display: "block",
                cursor: "pointer",
                objectFit: "contain",
              }}
            />
          </Box>
        </DialogContent>
      </Box>
      {/* 閉じる操作は画像右上の×アイコンに一本化。ここは「今後表示しない」選択のみ。 */}
      <DialogActions sx={{ justifyContent: "flex-start", px: 2 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={dontShowAgainIds.has(banner.id)}
              onChange={(e) => {
                setDontShowAgain(banner.id, e.target.checked);
              }}
              size="small"
            />
          }
          label="今後表示しない"
        />
      </DialogActions>
    </Dialog>
  );
};
